import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchParticipants,
  fetchParticipantById,
  createOrUpdateParticipant,
  deleteParticipant,
} from "./participantAPI";

export const getParticipants = createAsyncThunk(
  "participants/get",
  async (params = {}, thunkAPI) => {
    try {
      return await fetchParticipants(params);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const getParticipantById = createAsyncThunk(
  "participants/getById",
  async (uuid, thunkAPI) => {
    try {
      return await fetchParticipantById(uuid);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const upsertParticipant = createAsyncThunk(
  "participants/createOrUpdate",
  async (data, thunkAPI) => {
    try {
      return await createOrUpdateParticipant(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const removeParticipant = createAsyncThunk(
  "participants/remove",
  async (uuid, thunkAPI) => {
    try {
      return await deleteParticipant(uuid);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

const initialState = {
  participants: [],
  participant: null,
  pagination: {
    page: 1,
    limit: 30,
    total: 0,
    totalPages: 1,
  },
  loading: false,
  error: null,
};

const participantsSlice = createSlice({
  name: "participants",
  initialState,
  reducers: {
    clearParticipant(state) {
      state.participant = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getParticipants.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getParticipants.fulfilled, (state, action) => {
        state.loading = false;
        state.participants = action.payload?.data ?? [];
        state.pagination =
          action.payload?.pagination ?? initialState.pagination;
      })
      .addCase(getParticipants.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.participants = [];
        state.pagination = initialState.pagination;
      })

      // Get participant by id
      .addCase(getParticipantById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getParticipantById.fulfilled, (state, action) => {
        state.loading = false;
        state.participant = action.payload;
      })
      .addCase(getParticipantById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(upsertParticipant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(upsertParticipant.fulfilled, (state, action) => {
        state.loading = false;

        const participant = action.payload;

        const index = state.participants.findIndex(
          (p) => p.uuid === participant.uuid,
        );

        if (index !== -1) {
          state.participants[index] = participant;
        } else {
          state.participants.unshift(participant);
        }

        state.participant = participant;
      })
      .addCase(upsertParticipant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      
      .addCase(removeParticipant.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeParticipant.fulfilled, (state, action) => {
        state.loading = false;
        state.participants = state.participants.filter(
          (p) => p.uuid !== action.payload.uuid,
        );
      })
      .addCase(removeParticipant.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearParticipant } = participantsSlice.actions;
export default participantsSlice.reducer;